import React from 'react';
import { PaginationProps } from '../../../types/DesignSystem/Pagination';
import Typography from '../Typography/Typography';
import { ChevronLeft, ChevronRight } from '../Iconography/Icons';
import { FOCUS_RING } from '../focusRing';
import { CONTROL_HEIGHT, resolveControlSize, ControlSize } from '../controlSize';
import { paginationRange } from './paginationRange';
import { formatCount } from '../formatCount';
import Dropdown from '../Dropdown/Dropdown';
import { withClassName } from '../cn';
import { retainingDisabled, useFocusHolder } from '../focusRetention';

/**
 * Pagination — the table's missing half.
 *
 * `Table` is the backbone of catalog, products and the ISR project table, and until
 * now every screen paginated it with `Common/PaginatorWithApi` — a react-paginate
 * wrapper that hardcodes two off-palette hexes (`#DFE1E6`, `#253858`) inside inline
 * SVGs where `tokenDiscipline` cannot see them.
 *
 * **Correction (2026-08-31).** This docblock used to say the old one "carries no
 * `aria-current`, no landmark, and no accessible names". Measured on the rendered
 * component, **all three are false**: react-paginate emits `aria-current` on the
 * active page, an `aria-label` on every link ("Page 1 is your current page", "Next
 * page"), and `role="navigation" aria-label="Pagination"` on its `<ul>`.
 *
 * The landmark claim survived one round of this correction and was still wrong: it
 * came from counting `<nav>` ELEMENTS, which is genuinely 0, and reading that as "no
 * landmark". A role is not an element. Recorded rather than quietly deleted, because
 * a migration argued from an overstated baseline is a migration nobody can check —
 * and this doc was the baseline. The real gap is the palette, the target size and the
 * missing row count, and that gap is large enough not to need help.
 *
 * What the measurement did find, on the same 42-item / 10-per-page set, is worse
 * than the a11y claim it replaces:
 *
 * | | `PaginatorWithApi` | this |
 * |---|---|---|
 * | inactive page number | `#DFE1E6` — **1.24:1** on white | `#505866` — 7.0:1 |
 * | active page chip | `#253858` at `opacity: .7` | `accent-bold` `#091caa` |
 * | target | 32 x 30 | 36 x 36, on the control grid |
 *
 * plus `Showing 26–50 of 312`, which the old footer had no equivalent for at all.
 *
 * `1.24:1` against SC 1.4.3's 4.5:1 — the page numbers were very nearly invisible.
 * The winning declaration is `.pagination > li > a { color: #DFE1E6 }` at
 * specificity (0,1,2), beating `.pagination a { color: #42526E }` at (0,1,1) three
 * rules below it in the same file, which is not something you see by reading either
 * rule. And `Pagination.css` already holds the brand blue — `.pagination-item-bg-blue`
 * sets `#091CAA` at full opacity — but a caller has to know to ask for the class, and
 * Catalog Management never did, so it got the washed `#253858`/`.7` default that also
 * fades the white numeral sitting on it.
 *
 * What this one does differently, each deliberately:
 *
 * - **Controlled, and data-free.** `page` in, `onPageChange(page)` out. Slicing the
 *   rows is the caller's job — a presentation component that filters your data owns
 *   a contract it cannot keep (server-side pagination has no array to slice).
 * - **`<nav>` with `aria-current="page"`.** A screen reader announces "current page,
 *   5" and can jump to the landmark; the old one exposed a bag of anonymous buttons.
 * - **First and last page always reachable, constant slot count** — see
 *   `paginationRange`.
 * - **Semantic roles, not palette steps** (`ink-secondary`, `surface-sunken`,
 *   `accent`): new components land on the role layer from day one (palette.ts
 *   `ROLES`).
 * - **On the control grid** (§ 39): buttons are real 28/36/44 controls, square at
 *   minimum, so a bare chevron is still a WCAG 2.5.8 target.
 *
 * Rendering rules at the edges: `totalItems <= 0` renders nothing (the empty state
 * is the table's job — `EmptyState` exists for it); one page renders the summary
 * alone (the count still informs) unless `showSummary` is off, in which case
 * nothing.
 */

const MIN_W: Record<ControlSize, string> = {
  sm: 'tw-min-w-7',
  md: 'tw-min-w-9',
  lg: 'tw-min-w-11',
};

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(function Pagination({
  page,
  totalItems,
  pageSize,
  onPageChange,
  siblingCount = 1,
  pageSizeOptions,
  onPageSizeChange,
  pageSizeLabel = 'Rows per page',
  showSummary = true,
  size = 'md',
  disabled = false,
  label = 'Pagination',
  previousLabel = 'Previous page',
  nextLabel = 'Next page',
  pageLabel = (n: number) => `Go to page ${n}`,
  className = '',
  id,
  'data-testid': dataTestId,
  ...rest
}, ref) {
  /* § 401 — before the early returns below: a hook must run on every render. */
  const { holder, bind } = useFocusHolder<string>();
  if (totalItems <= 0 || pageSize <= 0) return null;

  const pageCount = Math.ceil(totalItems / pageSize);
  /* Clamped for DISPLAY only. A caller holding page=9 after a filter shrank the set
     to 3 pages sees page 3 highlighted; telling them to change state from inside a
     render is not this component's call. */
  const current = Math.min(Math.max(page, 1), pageCount);

  /* § 305 — the rows-per-page Dropdown renders only when BOTH halves are given; a
     list of sizes with nobody to tell is not a control. */
  const showPageSize = !!pageSizeOptions?.length && !!onPageSizeChange;

  if (pageCount <= 1 && !showSummary && !showPageSize) return null;

  const rung = resolveControlSize(size);
  const first = (current - 1) * pageSize + 1;
  const last = Math.min(current * pageSize, totalItems);

  /* `select-none` (§ 235): paging is a rapid-click surface, and a double-clicked
     page number flashed selected. Rides btnBase, so the aria-hidden ellipsis
     (which reuses it) stops being selectable dead text too. */
  const btnBase =
    `tw-flex tw-items-center tw-justify-center tw-px-1 tw-rounded-ctl tw-text-title tw-tabular-nums tw-select-none ` +
    `${CONTROL_HEIGHT[rung]} ${MIN_W[rung]} ${FOCUS_RING}`;
  const idle = 'tw-text-ink-secondary hover:tw-bg-surface-hover-strong hover:tw-text-ink tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift';
  /* § 401 — GrayText under forced colors: the browser gives it only to a NATIVELY disabled
     button, and the one holding focus is `aria-disabled`. Unchanged for the native ones. */
  const off = 'tw-text-ink-disabled tw-cursor-not-allowed forced-colors:tw-text-[color:GrayText]';

  const testid = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);
  /*
    § 401 — the button you pressed keeps focus when the press disables it. Next onto the last
    page disables Next; a page button under `disabled` (Catalog Management sets it for every
    fetch) disables the page you just chose. Natively disabled while focused, the browser moved
    focus to <body>. The focused one renders `aria-disabled` instead, and each click checks.
  */
  const prevOff = disabled || current === 1;
  const nextOff = disabled || current === pageCount;

  return (
    <nav
      {...rest}
      ref={ref}
      id={id}
      aria-label={label}
      className={withClassName(`tw-flex tw-items-center tw-justify-between tw-gap-4`, className)}
      data-testid={dataTestId}
    >
      {(showSummary || showPageSize) && (
        <div className="tw-flex tw-items-center tw-gap-3">
          {showPageSize && (
            <Dropdown
              label=""
              aria-label={pageSizeLabel}
              options={pageSizeOptions!.map((n) => ({ value: n, label: `${formatCount(n)} per page` }))}
              value={pageSize}
              onChange={(next) => onPageSizeChange!(Number(next))}
              size={rung}
              disabled={disabled}
              className="tw-w-36"
              data-testid={testid('page-size')}
            />
          )}
          {showSummary && (
            <Typography
              variant="caption-metadata"
              className="tw-text-ink-tertiary tw-tabular-nums"
              data-testid={testid('summary')}
            >
              {/* § 303 — locale-formatted: "of 12,345", not "of 12345" (formatCount.ts). */}
              {`Showing ${formatCount(first)}–${formatCount(last)} of ${formatCount(totalItems)}`}
            </Typography>
          )}
        </div>
      )}

      {pageCount > 1 && (
        <ol className="tw-flex tw-items-center tw-gap-1 tw-list-none tw-m-0 tw-p-0">
          <li>
            <button
              type="button"
              className={`${btnBase} ${prevOff ? off : idle}`}
              {...retainingDisabled(prevOff, holder === 'prev')}
              {...bind('prev')}
              onClick={() => { if (!prevOff) onPageChange(current - 1); }}
              aria-label={previousLabel}
              data-testid={testid('prev')}
            >
              <ChevronLeft aria-hidden="true" />
            </button>
          </li>

          {paginationRange(current, pageCount, siblingCount).map((item) =>
            typeof item === 'number' ? (
              <li key={item}>
                <button
                  type="button"
                  /*
                    `disabled` does NOT erase the current-page marker (2026-08-31).

                    It used to: `disabled ? off : item === current ? …` put the
                    disabled branch first, so every button — including the one you are
                    standing on — went flat grey. That was invisible while no caller
                    passed `disabled`; Catalog Management's footer now sets it for the
                    ~300ms a page fetch takes, and the result was that "which page am
                    I on" vanished on every single page turn, which is precisely when
                    a user is looking for it.

                    Being unavailable and being current are different facts. Disabling
                    suppresses the ACTION; it does not make the state uncertain. So the
                    accent fill is checked first and the disabled branch only applies
                    to the others.
                  */
                  className={`${btnBase} ${
                    item === current
                      /* § 378 — forced colors replaces the fill that marks the current page;
                         the inset Highlight outline is what marks it there. */
                      ? `tw-bg-accent-bold tw-text-ink-inverse forced-colors:tw-outline forced-colors:tw-outline-2 forced-colors:-tw-outline-offset-2 forced-colors:tw-outline-[color:Highlight]${disabled ? ' tw-cursor-not-allowed forced-colors:tw-text-[color:GrayText]' : ''}`
                      : disabled ? off : idle
                  }`}
                  {...retainingDisabled(disabled, holder === `page-${item}`)}
                  {...bind(`page-${item}`)}
                  onClick={() => { if (!disabled) onPageChange(item); }}
                  aria-label={pageLabel(item)}
                  aria-current={item === current ? 'page' : undefined}
                  data-testid={testid(`page-${item}`)}
                >
                  {formatCount(item)}
                </button>
              </li>
            ) : (
              <li key={item}>
                {/* Not a control and not read out — the numbers either side say it all. */}
                <span
                  aria-hidden="true"
                  className={`${btnBase.replace(FOCUS_RING, '')} tw-text-ink-tertiary`}
                >
                  &#8230;
                </span>
              </li>
            ),
          )}

          <li>
            <button
              type="button"
              className={`${btnBase} ${nextOff ? off : idle}`}
              {...retainingDisabled(nextOff, holder === 'next')}
              {...bind('next')}
              onClick={() => { if (!nextOff) onPageChange(current + 1); }}
              aria-label={nextLabel}
              data-testid={testid('next')}
            >
              <ChevronRight aria-hidden="true" />
            </button>
          </li>
        </ol>
      )}
    </nav>
  );
});

export default Pagination;
